import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Loader2, ArrowLeft, Shield, Package, Calendar, AlertCircle,
  CheckCircle2, Clock, Wrench,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

// Per-barcode history: manufacture → stock → dispatch → install → service visits.
// Shows a lifecycle summary at top + embeds a timeline (same visual language
// as ServiceAuditPage).

interface ProductUnitFull {
  id: string;
  code: string;
  serialNumber?: string | null;
  productId?: string;
  product?: {
    id: string;
    name: string;
    sku?: string;
    specifications?: { warrantyMonths?: number } | null;
  } | null;
  manufacturedAt?: string | null;
  dispatchedAt?: string | null;
  installedAt?: string | null;
  activatedAt?: string | null;
  warrantyStartDate?: string | null;
  warrantyEndDate?: string | null;
  installLocation?: string | null;
}

interface TimelineEvent {
  id: string;
  at: string;
  title: string;
  description?: string;
  kind: 'manufacture' | 'stock' | 'dispatch' | 'install' | 'service' | 'warranty';
}

export function ProductUnitWarrantyHistoryPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [unit, setUnit] = useState<ProductUnitFull | null>(null);
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!code) return;
    try {
      setLoading(true);
      setError(null);
      const uRes = await api.get(`/product-units/${encodeURIComponent(code)}/lookup`);
      const u: ProductUnitFull = uRes.data.data?.productUnit ?? uRes.data.data ?? uRes.data;
      setUnit(u);

      if (u?.id) {
        try {
          const eRes = await api.get(`/product-units/${u.id}/activity`);
          const rows = (eRes.data.data?.activities ?? eRes.data.data ?? []) as Array<Record<string, unknown>>;
          setEvents(rows.map(r => ({
            id: String(r.id || Math.random()),
            at: String(r.createdAt || r.at || ''),
            title: String(r.action || r.title || 'Event'),
            description: (r.notes as string) || (r.description as string) || '',
            kind: mapKind(String(r.action || '')),
          })));
        } catch {
          setEvents([]);
        }
      }
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Failed to load unit';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  if (error || !unit) {
    return (
      <div className="mx-auto max-w-xl rounded-xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700">
        <AlertCircle size={24} className="mx-auto mb-2" />
        {error || 'Unit not found'}
      </div>
    );
  }

  const warrantyActive =
    unit.warrantyEndDate && new Date(unit.warrantyEndDate).getTime() > Date.now();
  const warrantyMonths = unit.product?.specifications?.warrantyMonths ?? 12;

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Unit {unit.code}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {unit.product?.name}
          </p>
        </div>
      </div>

      {/* Summary card */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <SummaryCard
          icon={<Package size={18} />}
          label="Product"
          value={unit.product?.name || '—'}
          sub={unit.product?.sku}
        />
        <SummaryCard
          icon={<Calendar size={18} />}
          label="Installed"
          value={unit.installedAt ? formatDate(unit.installedAt) : 'Not yet'}
          sub={unit.installLocation || undefined}
        />
        <div className={`rounded-xl border p-4 ${
          warrantyActive
            ? 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950'
            : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'
        }`}>
          <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <Shield size={14} />
            Warranty
          </div>
          {warrantyActive ? (
            <>
              <div className="text-lg font-bold text-green-800 dark:text-green-200">
                Active
              </div>
              <div className="text-xs text-green-700 dark:text-green-300">
                Until {formatDate(unit.warrantyEndDate!)} · {warrantyMonths}m plan
              </div>
            </>
          ) : unit.warrantyEndDate ? (
            <>
              <div className="text-lg font-bold text-gray-500 dark:text-gray-400">Expired</div>
              <div className="text-xs text-gray-500">Ended {formatDate(unit.warrantyEndDate)}</div>
            </>
          ) : (
            <>
              <div className="text-lg font-bold text-gray-500 dark:text-gray-400">Not activated</div>
              <div className="text-xs text-gray-500">Complete installation to activate</div>
            </>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
        <h2 className="mb-4 text-sm font-semibold text-gray-900 dark:text-gray-100">Lifecycle</h2>
        {events.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500">
            <Clock size={24} className="mx-auto mb-2 text-gray-300" />
            No activity recorded yet.
          </div>
        ) : (
          <ol className="relative ml-3 border-l border-gray-200 dark:border-gray-700">
            {events.map(ev => (
              <li key={ev.id} className="mb-5 ml-4">
                <div className={`absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full ${kindColor(ev.kind)} text-white`}>
                  <Wrench size={9} />
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                  <div className="mb-0.5 flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
                      {ev.title.replace(/_/g, ' ')}
                    </div>
                    <time className="text-xs text-gray-400">{formatDate(ev.at)}</time>
                  </div>
                  {ev.description && (
                    <div className="text-xs text-gray-600 dark:text-gray-400">{ev.description}</div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="mt-6 text-center text-xs text-gray-500">
        <Link to="/service-tickets" className="text-[var(--ink-0)] hover:underline">
          See all tickets
        </Link>
      </div>
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {icon}
        {label}
      </div>
      <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{value}</div>
      {sub && <div className="text-xs text-gray-500 dark:text-gray-400">{sub}</div>}
    </div>
  );
}

function mapKind(action: string): TimelineEvent['kind'] {
  const s = action.toLowerCase();
  if (s.includes('manufactur')) return 'manufacture';
  if (s.includes('stock') || s.includes('inward') || s.includes('receiv')) return 'stock';
  if (s.includes('dispatch') || s.includes('ship')) return 'dispatch';
  if (s.includes('install') || s.includes('activat')) return 'install';
  if (s.includes('warrant')) return 'warranty';
  return 'service';
}

function kindColor(k: TimelineEvent['kind']): string {
  switch (k) {
    case 'manufacture': return 'bg-slate-500';
    case 'stock': return 'bg-blue-500';
    case 'dispatch': return 'bg-indigo-500';
    case 'install': return 'bg-green-500';
    case 'warranty': return 'bg-emerald-600';
    case 'service': return 'bg-amber-500';
    default: return 'bg-gray-400';
  }
}

// Small marker so CheckCircle2 import is useful; also used as verified chip.
export const _ok = CheckCircle2;
